import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useProfile } from "./ProfileContext";
import { useVisibleSidebarNavFeatures } from "@/hooks/use-sidebar-nav-features";
import {
  PRODUCT_SWITCH_ORDER,
  type SidebarNavGroupId,
} from "@/config/sidebar-nav";
import {
  PRODUCT_LANDING_PATH,
  getActiveProductForPathname,
  getStoredActiveProduct,
  setStoredActiveProduct,
} from "@/lib/product-scope";

interface ProductScopeContextValue {
  /**
   * Products the business currently has access to, Coach first — same set
   * the product switcher lists and the same entitlement rules the sidebar
   * nav and route guards already use (derived from the visible nav features,
   * so this can never disagree with what actually renders).
   */
  availableProducts: SidebarNavGroupId[];
  /** The product whose nav group and settings should show right now. */
  activeProduct: SidebarNavGroupId;
  /** Switches product: persists the choice and navigates to its landing page. */
  switchProduct: (product: SidebarNavGroupId) => void;
}

const ProductScopeContext = createContext<ProductScopeContextValue | undefined>(
  undefined,
);

/**
 * Resolves and persists the "active product" (GRA-78): Coach, Recruit and
 * Leads as separate workspaces. The active product follows the route
 * whenever the route owns one (`/coach/**`, `/recruit/**`, `/leads/**`); on
 * product-agnostic pages (Settings, Apps, More, …) it falls back to the last
 * product the user was on, remembered per signed-in user.
 */
export function ProductScopeProvider({ children }: { children: ReactNode }) {
  const { memberProfile } = useProfile();
  const uid = memberProfile?.uid ?? "";
  const location = useLocation();
  const visibleFeatures = useVisibleSidebarNavFeatures();

  const availableProducts = useMemo<SidebarNavGroupId[]>(
    () =>
      PRODUCT_SWITCH_ORDER.filter((product) =>
        visibleFeatures.some((f) => f.group === product && f.url),
      ),
    [visibleFeatures],
  );

  const routeProduct = getActiveProductForPathname(location.pathname);
  const storedProduct = uid ? getStoredActiveProduct(uid) : null;

  const activeProduct: SidebarNavGroupId = useMemo(() => {
    if (routeProduct && availableProducts.includes(routeProduct)) {
      return routeProduct;
    }
    if (storedProduct && availableProducts.includes(storedProduct)) {
      return storedProduct;
    }
    return availableProducts[0] ?? "coach";
  }, [routeProduct, storedProduct, availableProducts]);

  // Remember the product every time the route unambiguously picks one, so
  // product-agnostic pages (and the next "/" or post-login landing) fall
  // back to the one the user was actually using. Re-writing the same value
  // is harmless, so this doesn't need to compare against the stored one.
  useEffect(() => {
    if (!uid || !routeProduct) return;
    if (!availableProducts.includes(routeProduct)) return;
    setStoredActiveProduct(uid, routeProduct);
  }, [uid, routeProduct, availableProducts]);

  const navigate = useNavigate();
  const switchProduct = (product: SidebarNavGroupId) => {
    if (uid) setStoredActiveProduct(uid, product);
    navigate(PRODUCT_LANDING_PATH[product]);
  };

  return (
    <ProductScopeContext.Provider
      value={{ availableProducts, activeProduct, switchProduct }}
    >
      {children}
    </ProductScopeContext.Provider>
  );
}

export function useProductScope(): ProductScopeContextValue {
  const ctx = useContext(ProductScopeContext);
  if (!ctx) {
    throw new Error("useProductScope must be used within a ProductScopeProvider");
  }
  return ctx;
}
